import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { jobApi } from "../../../modules/jobs/api";
import { JobSettings, ScheduleJobRequest, UpdateJobSettings } from "../../../modules/jobs/types";
import { EntityObject, PageRequest, TwoLevelEntityIdObject } from "../../types";

export const JOBS_QUERY_KEYS = {
  getJobs: () => ["getJobs"],
  updateJob: () => ["updateJob"],
  scheduleJob: () => ["scheduleJob"],
  filterJobRecords: (payload?: EntityObject<PageRequest>) =>
    payload ? ["filterJobRecords", payload] : ["filterJobRecords"],
  cancelJob: () => ["cancelJob"],
  abortJob: () => ["abortJob"],
};

export const useGetJobs = () => {
  return useQuery({
    queryKey: JOBS_QUERY_KEYS.getJobs(),
    queryFn: async () => {
      const { data } = await jobApi.getJobs();

      return data;
    },
  });
};

export const useUpdateJob = ({ globalSpinner = true } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: JOBS_QUERY_KEYS.updateJob(),
    meta: { globalSpinner },
    mutationFn: async (payload: EntityObject<UpdateJobSettings>) => {
      const { data } = await jobApi.updateJob(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(JOBS_QUERY_KEYS.getJobs(), (oldData: JobSettings[]): JobSettings[] =>
        oldData.map((job) => {
          if (job.id === mutatedPayload.id) {
            return data;
          }

          return job;
        }),
      );
      Notification.itemUpdatedNotification();
    },
  });
};

export const useScheduleJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: JOBS_QUERY_KEYS.scheduleJob(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<ScheduleJobRequest>) => {
      const { data } = await jobApi.scheduleJob(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.getJobs() });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.getJobs() });
        queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.filterJobRecords() });
      }, 2500);

      Notification.itemCreatedNotification();
    },
  });
};

export const useFilterJobRecords = (payload?: EntityObject<PageRequest>) => {
  return useQuery({
    queryKey: JOBS_QUERY_KEYS.filterJobRecords(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await jobApi.filterJobRecords(payload!);

      return data;
    },
    enabled: !!payload,
    placeholderData: keepPreviousData,
  });
};

export const useCancelJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: JOBS_QUERY_KEYS.cancelJob(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const { data } = await jobApi.cancelJob(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.getJobs() });
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.filterJobRecords() });
    },
  });
};

export const useAbortJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: JOBS_QUERY_KEYS.abortJob(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const { data } = await jobApi.abortJob(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.getJobs() });
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.filterJobRecords() });
    },
  });
};
